import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { buildBreadcrumbSchema } from "@/components/seo/schemas";
import BrowseFilters from "@/components/browse/BrowseFilters";
import BrowseGrid from "@/components/browse/BrowseGrid";
import { useAllListings, useListingCities, useListingStates, useListingTags } from "@/hooks/use-listings";

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [city, setCity] = useState(searchParams.get("city") || "");
  const stateParam = searchParams.get("state") || "";
  const [zip, setZip] = useState(searchParams.get("zip") || "");
  const [service, setService] = useState(searchParams.get("service") || "");

  const updateParams = (updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, val] of Object.entries(updates)) {
      if (val) next.set(key, val);
      else next.delete(key);
    }
    setSearchParams(next);
  };

  const setStateFilter = (val: string) => updateParams({ state: val });

  const handleSearchChange = (val: string) => {
    setSearch(val);
    updateParams({ search: val });
  };

  const handleCityChange = (val: string) => {
    setCity(val);
    updateParams({ city: val });
  };

  const handleZipChange = (val: string) => {
    setZip(val);
    updateParams({ zip: val });
  };

  const handleServiceChange = (val: string) => {
    setService(val);
    updateParams({ service: val });
  };

  const handleReset = () => {
    setSearch("");
    setCity("");
    setZip("");
    setService("");
    setSearchParams(new URLSearchParams());
  };

  const { data: listings, isLoading } = useAllListings(
    search || undefined,
    city || undefined,
    stateParam || undefined,
    service || undefined,
    zip || undefined
  );
  const { data: cities } = useListingCities();
  const { data: states } = useListingStates();
  const { data: tags } = useListingTags();

  return (
    <Layout>
      <PageMeta
        title="Browse 700+ MedSpas Near You — Compare Treatments & Reviews"
        description="Browse our directory of 700+ verified medical spas across the U.S. Filter by city, state, or treatment — Botox, fillers, laser, CoolSculpting, microneedling and more. Read reviews, compare hours, and contact providers free."
        path="/browse"
        jsonLd={buildBreadcrumbSchema([
          { name: "Home", url: "https://medspadirectorypro.com/" },
          { name: "Browse", url: "https://medspadirectorypro.com/browse" },
        ])}
      />
      <div className="bg-card/50 border-b border-border">
        <div className="container mx-auto px-4 lg:px-8 py-10 lg:py-14">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Left - Image/Illustration placeholder */}
            <div className="relative rounded-2xl border border-border overflow-hidden h-40 lg:h-auto">
              <img
                src="https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Fcc449eaa12f04ec8a633e5d5dc5c0623"
                alt="MedSpa Directory Pro reception"
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <svg className="w-8 h-8 mb-1.5 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-sm font-medium drop-shadow-lg">Browse & Discover</p>
              </div>
            </div>

            {/* Right - Header and Filters */}
            <div className="lg:col-span-2 flex flex-col justify-center">
              <h1 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Browse <span className="bg-gradient-to-r from-blue-500 via-primary to-teal-400 bg-clip-text text-transparent">MedSpas</span>
              </h1>
              <p className="text-muted-foreground mb-6">
                {isLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading listings...
                  </span>
                ) : (
                  `${listings?.length ?? 0} MedSpas Found`
                )}
              </p>
              <BrowseFilters
                search={search}
                onSearchChange={handleSearchChange}
                city={city}
                onCityChange={handleCityChange}
                cities={cities ?? []}
                state={stateParam}
                onStateChange={setStateFilter}
                states={states ?? []}
                zip={zip}
                onZipChange={handleZipChange}
                service={service}
                onServiceChange={handleServiceChange}
                services={tags ?? []}
                onReset={handleReset}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <BrowseGrid listings={listings ?? []} isLoading={isLoading} />
      </div>
    </Layout>
  );
}
